var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    Control                     = require("enyo/Control"),
    Signals                     = require("enyo/Signals"),
    kind                        = require("enyo/kind"),
    logger                      = require("enyo/logger");

module.exports = kind(/** @lends  app.MediaPlayer.Tech.Astract.AbstractPlayerTech.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.Tech.Astract.AbstractPlayerTech",

    /**
     * @private
     */
    mixins: [AbsoluteShowingQueueSupport],

    /**
     * @private
     */
    classes: "app-media-player-player-tech",

    // Capabilities
    // --------------------------------

    /**
     * Whether this player tech can apply safe area styling to it's UI.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    supportsSafeArea: false,

    /**
     * Flag whether this player tech can in principle be reused for any other
     * media items at all. This flag will be used to determine if the tech
     * should be destroyed once unmounted from the DOM.
     *
     * @type  {boolean}
     * @public
     */
    isReusable: false,

    /**
     * When using 3rd party players, it's highly recommended to test if they are
     * reliably reusable after detaching them from and re-attaching them to the
     * DOM. Some (YouTube, for example) aren't reliably reusable once they have
     * been detached.
     *
     * It's recommended to explicitly set the `isReusable` flag to `false`
     * whenever a state is encountered that permanently prevents the tech from
     * being re-usable. This will ensure the tech gets auto-destroyed once it
     * gets un-mounted.
     *
     * This method will be called to determine if this tech can be reused for
     * the provided media item and other watching session details (for example,
     * ad schedule).
     *
     * @param    {?Object}  mediaData    The media data
     * @param    {?Object}  sessionData  The watching session
     * @returns  {boolean}  Whether this tech can play the supplied media
     * @public
     */
    isReusableFor: function() {
        var errorMsg = "app.MediaPlayer.Tech.AbstractPlayerTech.update() not implemented";

        logger.warn();

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }

        return this.isReusable;
    },

    /**
     * Whether to use a tracker.
     *
     * @type  {boolean}
     * @public
     */
    useTracker: false,

    // Forward events to Media Controller
    // --------------------------------

    /**
     * @private
     */
    handlers: {
        onMediaPlayerError:          "onMediaPlayerError",
        onProgressTrackerError:      "onProgressTrackerError",
        onRequestShowLoadingGraphic: "onRequestShowLoadingGraphic",
        onRequestHideLoadingGraphic: "onRequestHideLoadingGraphic"
    },

    /**
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop event propagation
     * @private
     */
    onMediaPlayerError: function(sender, event) {
        this.controller.triggerHandler("onMediaPlayerError", sender, event);

        return true;
    },

    /**
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop event propagation
     * @private
     */
    onProgressTrackerError: function(sender, event) {
        this.controller.triggerHandler("onProgressTrackerError", sender, event);

        return true;
    },

    /**
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop event propagation
     * @private
     */
    onRequestShowLoadingGraphic: function(sender, event) {
        this.controller.triggerHandler("onRequestShowLoadingGraphic", sender, event);

        return true;
    },

    /**
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop event propagation
     * @private
     */
    onRequestHideLoadingGraphic: function(sender, event) {
        this.controller.triggerHandler("onRequestHideLoadingGraphic", sender, event);

        return true;
    },

    // Common Components
    // --------------------------------

    /**
     * The tracker kind to use.
     *
     * @type  {Function}
     * @public
     */
    trackerKind: null,

    /**
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.createComponents([{
                kind:               Signals,
                onFullscreenChange: "onFullscreenChange"
            }]);

            if (this.trackerKind) {
                this.createComponent({
                    name:       "tracker",
                    kind:       this.trackerKind,
                    controller: this.controller,
                    playerTech: this
                });
            }
        };
    }),

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.onFullscreenChange();
        };
    }),

    // Internal State
    // --------------------------------

    /**
     * Determines if the player is fully initialized and ready to be used.
     * Possible values:
     *  * -1 = no player
     *  *  0 = player initializing
     *  *  1 = player ready
     *
     * @type     {number}
     * @default  -
     * @readonly
     * @public
     */
    isReady: -1,

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPlaying: false,

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    fullscreen: false,

    /**
     * @private
     */
    viewLoadingGraphicRequests: 0,

    /**
     * Resets player to the state of an uninitialized player.
     *
     * @protected
     */
    resetPlayerState: function() {
        this.set("isReady", -1);
        this.set("isPlaying", false);
    },

    /**
     * Shows the scrim and spinner while reader is activating, hides them
     * otherwise.
     *
     * This method displays both the loading graphic in the app level ánd the
     * one in the playerView. This ensures the app's main loading graphic is
     * used when the reader is maximized, while still displaying a local loading
     * graphic when the reader isn't maximized.
     *
     * @param  {boolean}  wasReady  Whether the reader was ready before.
     * @protected
     */
    isReadyChanged: function(wasReady) {
        var isReady = this.get("isReady");

        this.app.$.state.set("mediaPlayerReady", isReady);

        // TODO: Can this lead to some type of infinite loading state?
        // Show spinner when loading
        if (wasReady !== 0 && isReady === 0) {
            this.controller.triggerHandler("onRequestShowLoadingGraphic");
        }
        else if (wasReady === 0 && isReady !== 0) {
            this.controller.triggerHandler("onRequestHideLoadingGraphic");
        }
    },

    /**
     * @protected
     */
    isPlayingChanged: function() {
        this.app.$.state.set("mediaPlayerPlaying", this.get("isPlaying"));
    },

    /**
     * Listen to fullscreen change signal.
     *
     * @protected
     */
    onFullscreenChange: function() {
        var node              = this.hasNode(),
            fullscreenElement = Control.Fullscreen.getFullscreenElement();

        if (node && fullscreenElement && (
            node === fullscreenElement
            || node.contains(fullscreenElement)
        )) {
            this.setAttribute("data-fullscreen", "on");
            this.set("fullscreenElement", fullscreenElement);
            this.set("fullscreen", true);
        }
        else {
            this.setAttribute("data-fullscreen", "off");
            this.set("fullscreenElement", null);
            this.set("fullscreen", false);
        }

        // Fullscreen change is triggered after the reflow, so we need to reflow
        // again.
        this.resize();
    },

    /**
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            // Ensures we trigger a request to hide the loading graphic.
            this.set("isReady", -1);
            sup.apply(this, arguments);
        };
    })

});
